import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  User, Mail, Phone, Globe, FileText, Shield, 
  LogOut, Settings, ChevronRight, ShieldCheck, 
  AlertCircle, Gift, Copy, Check, Users as UsersIcon, X, TrendingUp
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { supabase } from '../supabaseClient';
import Layout from '../components/Layout';
import NotificationSettingsCard from '../components/NotificationSettingsCard';

const Profile = () => {
  const navigate = useNavigate();
  const { user, logout } = useApp();
  
  // Estados para el sistema de referidos
  const [referrals, setReferrals] = useState([]);
  const [copiedCode, setCopiedCode] = useState(false);
  const [loadingRefs, setLoadingRefs] = useState(false);
  const [showReferralsModal, setShowReferralsModal] = useState(false);
  
  // Estado para los toques secretos
  const [secretClicks, setSecretClicks] = useState(0);

  useEffect(() => {
    if (user?.referral_code) {
      fetchReferrals();
    }
  }, [user]);

  // Manejo del contador de toques secretos
  useEffect(() => {
    if (secretClicks === 5) {
      setSecretClicks(0); // Reiniciamos por si vuelve
      navigate('/tasas');
    }
    
    // Si deja de tocar, el contador se reinicia después de 2 segundos
    let timeout;
    if (secretClicks > 0 && secretClicks < 5) {
      timeout = setTimeout(() => {
        setSecretClicks(0);
      }, 2000);
    }
    return () => clearTimeout(timeout);
  }, [secretClicks, navigate]);

  const handleSecretClick = () => {
    setSecretClicks(prev => prev + 1);
  };

  const fetchReferrals = async () => {
    setLoadingRefs(true);
    const { data, error } = await supabase
      .from('referrals')
      .select(`
        created_at,
        first_transaction_done,
        referred:referred_id ( first_name, last_name, phone )
      `)
      .eq('referrer_id', user.id)
      .order('created_at', { ascending: false });

    if (!error && data) {
      setReferrals(data);
    }
    setLoadingRefs(false);
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const handleCopyCode = () => {
    if (!user?.referral_code) return;
    navigator.clipboard.writeText(user.referral_code);
    setCopiedCode(true);
    setTimeout(() => setCopiedCode(false), 2000);
  };

  const profileItems = [
    { icon: User, label: 'Nombre completo', value: `${user?.first_name || ''} ${user?.last_name || ''}`.trim() || '—' },
    { icon: Mail, label: 'Correo electrónico', value: user?.email || 'No registrado' },
    { icon: Phone, label: 'Teléfono', value: user?.phone || 'No registrado' },
    { icon: Globe, label: 'País', value: user?.country || 'No registrado', isSecretTarget: true }
  ];

  const menuItems = [
    { icon: FileText, label: 'Términos y Condiciones', action: () => {} },
    { icon: Shield, label: 'Políticas de Seguridad', action: () => {} },
    ...(user?.role?.toLowerCase() === 'administrador'
      ? [{ icon: Settings, label: 'Panel de Administración', action: () => navigate('/admin'), highlight: true }]
      : []),
    { icon: LogOut, label: 'Cerrar Sesión', action: handleLogout, danger: true }
  ];

  const getInitials = () => {
    if (user?.first_name && user?.last_name) return `${user.first_name[0]}${user.last_name[0]}`.toUpperCase();
    return user?.first_name ? user.first_name[0].toUpperCase() : 'U';
  };

  const totalInvitados = referrals.length;
  const metasCumplidas = referrals.filter(r => r.first_transaction_done).length;

  return (
    <Layout>
      <div className="min-h-screen bg-[#f4f7fb] px-4 sm:px-6 py-8 md:py-12 pb-32">
        <div className="mx-auto w-full max-w-lg">
          
          {/* HEADER / AVATAR */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="flex flex-col items-center mb-10 mt-4"
          >
            <div className="relative mb-5">
              <div className="w-28 h-28 md:w-32 md:h-32 bg-gradient-to-br from-blue-600 to-blue-900 rounded-[32px] flex items-center justify-center shadow-lg shadow-blue-900/20 transform rotate-3 transition-transform hover:rotate-0 duration-300">
                <span className="text-4xl md:text-5xl font-black text-white tracking-tighter -rotate-3">
                  {getInitials()}
                </span>
              </div>
              <div className="absolute -bottom-2 -right-2 bg-white p-1 rounded-xl shadow-sm border border-slate-100">
                {user?.is_verified ? (
                  <div className="bg-green-100 text-green-600 p-1.5 rounded-lg"><ShieldCheck className="w-5 h-5" /></div>
                ) : (
                  <div className="bg-amber-100 text-amber-600 p-1.5 rounded-lg"><AlertCircle className="w-5 h-5" /></div>
                )}
              </div>
            </div>

            <h1 className="text-2xl md:text-3xl font-black text-slate-800 tracking-tight leading-tight">
              {user?.first_name || 'Usuario'} {user?.last_name || ''}
            </h1>
            
            {user?.is_verified ? (
              <div className="inline-flex items-center gap-1.5 mt-3 px-3 py-1.5 bg-green-50 text-green-700 rounded-full border border-green-100 shadow-sm">
                <div className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse"></div>
                <span className="text-[10px] font-black uppercase tracking-widest">Cuenta Verificada</span>
              </div>
            ) : (
              <div className="inline-flex items-center gap-1.5 mt-3 px-3 py-1.5 bg-amber-50 text-amber-700 rounded-full border border-amber-100 shadow-sm">
                <div className="w-1.5 h-1.5 bg-amber-500 rounded-full"></div>
                <span className="text-[10px] font-black uppercase tracking-widest">No Verificada</span>
              </div>
            )}
          </motion.div>

          {/* MENSAJE NO VERIFICADO */}
          {!user?.is_verified && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.1 }}
              className="bg-gradient-to-r from-amber-50 to-amber-100 border border-amber-200 rounded-[24px] p-5 mb-8 flex items-start gap-4 shadow-sm"
            >
              <div className="bg-white rounded-full p-2 text-amber-500 shadow-sm shrink-0"><Shield className="w-5 h-5" /></div>
              <div>
                <h3 className="font-black text-amber-900 text-sm tracking-tight mb-1">Tu cuenta no está verificada</h3>
                <p className="text-[11px] font-bold text-amber-700/80 leading-relaxed">Para disfrutar de mayores límites de envío, contacta a soporte.</p>
              </div>
            </motion.div>
          )}

          <NotificationSettingsCard userId={user?.id} />

          {/* INFORMACIÓN PERSONAL */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="bg-white rounded-[32px] p-6 md:p-8 shadow-sm border border-slate-100 mb-8"
          >
            <h2 className="text-[11px] font-black uppercase tracking-widest text-slate-400 mb-6 flex items-center gap-2">
              <User className="w-4 h-4" /> Datos de la cuenta
            </h2>
            <div className="space-y-0">
              {profileItems.map((item, index) => {
                const Icon = item.icon;
                const isLast = index === profileItems.length - 1;
                return (
                  <div key={index} className={`flex items-center gap-4 py-4 ${!isLast ? 'border-b border-dashed border-slate-100' : ''}`}>
                    <div 
                      onClick={item.isSecretTarget ? handleSecretClick : undefined}
                      className={`w-12 h-12 bg-slate-50 rounded-[16px] flex items-center justify-center flex-shrink-0 border border-slate-100 ${item.isSecretTarget ? 'cursor-pointer active:scale-90 transition-transform select-none' : ''}`}
                    >
                      <Icon className="w-5 h-5 text-blue-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">{item.label}</p>
                      <p className="font-bold text-slate-800 truncate text-[15px] mt-0.5">{item.value}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </motion.div>

          {/* 🎁 PANEL DE REFERIDOS */}
          {user?.referral_code && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2 }}
              className="bg-gradient-to-br from-indigo-600 to-purple-800 rounded-[32px] p-6 md:p-8 shadow-xl shadow-indigo-900/20 mb-8 relative overflow-hidden text-white"
            >
              <div className="absolute -top-12 -right-12 w-40 h-40 bg-white/10 rounded-full blur-2xl pointer-events-none"></div>
              
              <div className="flex items-center gap-3 mb-6 relative z-10">
                <div className="p-2.5 bg-white/20 rounded-xl backdrop-blur-sm"><Gift className="w-6 h-6 text-indigo-50" /></div>
                <div>
                  <h3 className="font-black text-lg tracking-tight leading-tight">Programa de Promotores</h3>
                  <p className="text-[10px] font-black uppercase tracking-widest text-indigo-200 mt-0.5">Invita y gana</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 mb-6 relative z-10">
                <div className="bg-black/20 backdrop-blur-md rounded-[20px] p-4 border border-white/10">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-[10px] font-black uppercase tracking-widest text-indigo-200">Invitados</span>
                    <UsersIcon className="w-4 h-4 text-indigo-300" />
                  </div>
                  <span className="text-3xl font-black text-white">{totalInvitados}</span>
                </div>
                
                <div className="bg-black/20 backdrop-blur-md rounded-[20px] p-4 border border-white/10">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-[10px] font-black uppercase tracking-widest text-indigo-200">Envíos</span>
                    <TrendingUp className="w-4 h-4 text-green-400" />
                  </div>
                  <span className="text-3xl font-black text-green-300">{metasCumplidas}</span>
                </div>
              </div>

              <div className="bg-white/10 rounded-[20px] p-1.5 flex items-center justify-between backdrop-blur-md border border-white/20 relative z-10">
                <div className="pl-4 font-mono font-bold tracking-widest text-lg text-white">
                  {user.referral_code}
                </div>
                <button
                  onClick={handleCopyCode}
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-2xl font-bold text-sm transition-all active:scale-95 ${
                    copiedCode ? 'bg-green-500 text-white shadow-lg shadow-green-500/30' : 'bg-white text-indigo-900 hover:bg-indigo-50 shadow-md'
                  }`}
                >
                  {copiedCode ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  {copiedCode ? 'Copiado' : 'Copiar'}
                </button>
              </div>

              <button 
                onClick={() => setShowReferralsModal(true)}
                className="w-full mt-4 py-3 border border-white/20 rounded-2xl text-[11px] font-black uppercase tracking-widest text-indigo-100 hover:bg-white/10 transition-colors relative z-10"
              >
                Ver lista de invitados
              </button>
            </motion.div>
          )}

          {/* MENÚ DE AJUSTES */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="bg-white rounded-[32px] p-4 md:p-6 shadow-sm border border-slate-100"
          >
            <h2 className="text-[11px] font-black uppercase tracking-widest text-slate-400 mb-4 ml-4 mt-2 flex items-center gap-2">
              <Settings className="w-4 h-4" /> Ajustes y Seguridad
            </h2>
            <div className="space-y-2">
              {menuItems.map((item, index) => {
                const Icon = item.icon;
                return (
                  <button
                    key={index}
                    onClick={item.action}
                    className={`w-full flex items-center gap-4 p-4 rounded-2xl transition-all duration-200 active:scale-[0.98] ${
                      item.danger ? 'bg-red-50/50 hover:bg-red-50 group' : item.highlight ? 'bg-blue-50/50 hover:bg-blue-50 group' : 'bg-transparent hover:bg-slate-50 group'
                    }`}
                  >
                    <div className={`w-12 h-12 rounded-[16px] flex items-center justify-center flex-shrink-0 transition-colors ${
                      item.danger ? 'bg-red-100 text-red-600 group-hover:bg-red-600 group-hover:text-white' : item.highlight ? 'bg-blue-100 text-blue-600 group-hover:bg-blue-600 group-hover:text-white' : 'bg-slate-100 text-slate-500 group-hover:bg-slate-800 group-hover:text-white'
                    }`}>
                      <Icon className="w-5 h-5" />
                    </div>
                    <span className={`font-bold text-left flex-1 tracking-tight ${item.danger ? 'text-red-600' : item.highlight ? 'text-blue-900' : 'text-slate-700'}`}>
                      {item.label}
                    </span>
                    {!item.danger && <ChevronRight className={`w-5 h-5 transition-transform group-hover:translate-x-1 ${item.highlight ? 'text-blue-300' : 'text-slate-300'}`} />}
                  </button>
                );
              })}
            </div>
          </motion.div>

        </div>
      </div>

      {/* MODAL: VER REFERIDOS */}
      <AnimatePresence>
        {showReferralsModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm">
            <motion.div 
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="bg-white w-full max-w-md rounded-[32px] shadow-2xl overflow-hidden flex flex-col max-h-[85vh]"
            >
              <div className="p-6 border-b border-slate-100 flex items-center justify-between bg-slate-50">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-indigo-100 rounded-full flex items-center justify-center">
                    <UsersIcon className="w-5 h-5 text-indigo-600" />
                  </div>
                  <div>
                    <h3 className="font-black text-slate-800 text-lg leading-tight">Tus Invitados</h3>
                    <p className="text-[10px] font-black uppercase tracking-widest text-slate-400 mt-0.5">Historial completo</p>
                  </div>
                </div>
                <button 
                  onClick={() => setShowReferralsModal(false)}
                  className="w-8 h-8 bg-white border border-slate-200 rounded-full flex items-center justify-center text-slate-500 hover:bg-slate-100 transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              <div className="p-6 flex-1 overflow-y-auto">
                {loadingRefs ? (
                  <div className="flex flex-col items-center justify-center py-10">
                    <div className="w-8 h-8 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin mb-3"></div>
                    <p className="text-slate-400 text-xs font-bold uppercase tracking-widest">Cargando...</p>
                  </div>
                ) : referrals.length === 0 ? (
                  <div className="text-center py-10">
                    <div className="w-16 h-16 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-4 border border-dashed border-slate-200">
                      <Gift className="w-6 h-6 text-slate-300" />
                    </div>
                    <p className="font-bold text-slate-600 text-sm">Aún no tienes invitados</p>
                    <p className="text-slate-400 text-xs mt-1 px-4">Comparte tu código con amigos para que aparezcan aquí.</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {referrals.map((ref, idx) => (
                      <div key={idx} className="bg-white p-4 rounded-2xl border border-slate-100 shadow-sm flex items-center justify-between group hover:border-indigo-100 transition-colors">
                        <div>
                          <p className="font-bold text-slate-800 capitalize text-sm">
                            {ref.referred?.first_name} {ref.referred?.last_name}
                          </p>
                          <p className="text-[10px] font-black uppercase tracking-widest text-slate-400 mt-1">
                            {new Date(ref.created_at).toLocaleDateString()}
                          </p>
                        </div>
                        
                        <div className={`px-2.5 py-1.5 rounded-lg text-[9px] font-black uppercase tracking-widest flex items-center gap-1.5 ${
                          ref.first_transaction_done 
                            ? 'bg-green-50 text-green-600 border border-green-200' 
                            : 'bg-amber-50 text-amber-600 border border-amber-200'
                        }`}>
                          {ref.first_transaction_done ? <ShieldCheck className="w-3.5 h-3.5" /> : <AlertCircle className="w-3.5 h-3.5" />}
                          {ref.first_transaction_done ? 'Envío Exitoso' : 'Pendiente'}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

    </Layout>
  );
};

export default Profile;

